import { useMemo, useState } from "react";
import { AppButton } from "../components/ui/AppButton.js";
import {
  CUSTOMER_STATUS_COPY_UK,
  d1ClaimTg50,
  d1CreateCartDraft,
  d1CreateCheckoutDraft,
  d1GetOrderStatus,
  d1PlaceOrder,
  isD1CheckoutEnabled,
} from "./d1-client.js";
import { CHECKOUT_UI_SHELL_FLAG_NAME } from "./flag.js";
import { SHELL_STEP_ORDER, SHELL_STEP_TITLES, STAGING_GUARDRAIL_BANNER } from "./mock-data.js";
import { DemoBanner } from "./ShellChrome.js";
import { renderShellStep } from "./screens.js";
import type { ShellStepId } from "./types.js";

/**
 * Checkout / order UX shell — internal beta / staging only.
 * Optional D1 wiring when VITE_MINIAPP_D1_CHECKOUT_ENABLED=true.
 */
export function CheckoutUiShellApp() {
  const [index, setIndex] = useState(0);
  const d1Enabled = isD1CheckoutEnabled();
  const [sessionKey] = useState(() => `shell_${crypto.randomUUID().slice(0, 8)}`);
  const [cartId, setCartId] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [customerMsg, setCustomerMsg] = useState<string>("");
  const [log, setLog] = useState<string>("");
  const [busy, setBusy] = useState(false);

  const step: ShellStepId = SHELL_STEP_ORDER[index] ?? "cart_review";
  const atStart = index <= 0;
  const atEnd = index >= SHELL_STEP_ORDER.length - 1;
  const progressLabel = useMemo(
    () => `${index + 1} / ${SHELL_STEP_ORDER.length}`,
    [index],
  );

  async function runD1Flow() {
    setBusy(true);
    setLog("Running staging D1 test order…");
    try {
      const cart = await d1CreateCartDraft({
        sessionKey,
        catalogMode: "live",
        items: [
          {
            product_id: "STG-DEMO-001",
            title: "Staging demo A",
            qty: 2,
            unit_price_uah: 300,
            price_resolved: true,
            stock_status: "in_stock",
          },
          {
            product_id: "STG-DEMO-002",
            title: "Staging demo B",
            qty: 1,
            unit_price_uah: 250,
            price_resolved: true,
            stock_status: "in_stock",
          },
        ],
      });
      if (!cart.json.ok) {
        setLog(`Cart failed: ${JSON.stringify(cart.json)}`);
        return;
      }
      setCartId(String(cart.json.cart_id));

      const tg50 = await d1ClaimTg50(sessionKey, "+380000000000");
      const draft = await d1CreateCheckoutDraft({
        sessionKey,
        cartId: String(cart.json.cart_id),
        contactName: "Staging Demo",
        contactPhone: "+380000000000",
        couponCode: "TG50",
        catalogMode: "live",
      });
      if (!draft.json.ok) {
        setLog(`Draft failed: ${JSON.stringify(draft.json)} tg50=${JSON.stringify(tg50.json)}`);
        return;
      }
      setDraftId(String(draft.json.draft_id));

      const placed = await d1PlaceOrder({
        sessionKey,
        draftId: String(draft.json.draft_id),
        idempotencyKey: `shell_${sessionKey}`,
        catalogMode: "live",
      });
      if (!placed.json.ok) {
        setLog(`Place failed: ${JSON.stringify(placed.json)}`);
        return;
      }
      setOrderId(String(placed.json.order_id));
      const st = await d1GetOrderStatus(String(placed.json.order_id));
      const msg = st.json.customer_message as { key?: string; uk?: string } | undefined;
      const uk =
        msg?.uk ??
        (msg?.key ? CUSTOMER_STATUS_COPY_UK[msg.key] : undefined) ??
        CUSTOMER_STATUS_COPY_UK.payment_pending ??
        "";
      setCustomerMsg(uk);
      setLog(
        `TEST ORDER OK · order=${placed.json.order_id} · payment=${placed.json.payment_status} · ttn=${placed.json.ttn_status} · payment_live=false · np_live=false`,
      );
    } catch (err) {
      setLog(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-shell flex min-h-screen flex-col bg-[var(--tg-theme-bg-color,#ffffff)] text-[var(--tg-theme-text-color,#000000)]">
      <DemoBanner />
      <div className="border-b border-sky-300 bg-sky-50 px-4 py-2 text-center text-xs font-semibold text-sky-950">
        {STAGING_GUARDRAIL_BANNER}
      </div>
      <header className="sticky top-0 z-20 border-b border-black/10 bg-[var(--tg-theme-bg-color,#ffffff)]/95 px-4 py-3 backdrop-blur">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-700">
          AsiaStyle · Internal beta checkout shell
        </p>
        <h1 className="text-lg font-semibold leading-tight">{SHELL_STEP_TITLES[step]}</h1>
        <p className="text-xs opacity-60">
          Flag {CHECKOUT_UI_SHELL_FLAG_NAME} · D1={d1Enabled ? "on" : "off"} · step {progressLabel}
        </p>
      </header>

      <main className="mx-auto flex w-full max-w-[520px] flex-1 flex-col gap-4 px-4 py-4 pb-28">
        {d1Enabled ? (
          <section className="rounded-[16px] border border-sky-200 bg-sky-50/60 p-4">
            <h3 className="text-sm font-semibold">D1 staging test order</h3>
            <p className="mt-1 text-xs opacity-70">Internal beta · test session · not a public customer flow</p>
            <p className="mt-1 text-xs">
              cart={cartId ?? "—"} · draft={draftId ?? "—"} · order={orderId ?? "—"}
            </p>
            <p className="mt-1 text-[11px] font-medium text-amber-900">
              Payment not live · Nova Poshta / TTN not live · no real payment unless ops approved
            </p>
            {customerMsg ? (
              <p className="mt-2 rounded-lg border border-sky-200 bg-white/70 px-3 py-2 text-xs leading-relaxed">
                {customerMsg}
              </p>
            ) : null}
            <AppButton className="mt-3 w-full" disabled={busy} onClick={() => void runD1Flow()}>
              {busy ? "Working…" : "Create test cart → TG50 → draft → place (D1)"}
            </AppButton>
            {log ? <p className="mt-2 break-all text-xs opacity-80">{log}</p> : null}
          </section>
        ) : (
          <section className="rounded-[16px] border border-amber-200 bg-amber-50/70 p-4 text-xs text-amber-950">
            D1 staging actions off. Enable <code>VITE_MINIAPP_D1_CHECKOUT_ENABLED</code> for internal beta
            test orders. Preview steps below remain UI-only.
          </section>
        )}
        {renderShellStep(step)}
      </main>

      <footer className="fixed bottom-0 left-1/2 z-20 w-full max-w-[520px] -translate-x-1/2 border-t border-black/10 bg-[var(--tg-theme-bg-color,#ffffff)] px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="grid grid-cols-2 gap-2">
          <AppButton variant="secondary" disabled={atStart} onClick={() => setIndex((v) => v - 1)}>
            Back
          </AppButton>
          <AppButton disabled={atEnd} onClick={() => setIndex((v) => v + 1)}>
            Next
          </AppButton>
        </div>
      </footer>
    </div>
  );
}
